import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Punto di estensione (C-002, NON NEGOZIABILE): ogni futura tecnologia di lettura badge
 * (NFC, RFID, QR…) implementa questa interfaccia senza toccare la logica di autenticazione.
 */
export interface BadgeSource {
  readonly code$: Observable<string>;
}

/**
 * Intercetta la "raffica" rapida di tasti emessa da un lettore USB keyboard-wedge e la
 * distingue dalla digitazione umana tramite la soglia inter-tasto. Emette il codice via code$.
 */
@Injectable({ providedIn: 'root' })
export class BadgeReaderService implements BadgeSource {
  private readonly interKeyThresholdMs = 50;
  private readonly minCodeLength = 3;
  private readonly codeSubject = new Subject<string>();
  readonly code$ = this.codeSubject.asObservable();

  private buffer = '';
  private lastKeyTime = 0;
  private listener?: (event: KeyboardEvent) => void;
  // Conteggio dei consumatori attivi (ref-count): il listener globale è registrato sse e solo
  // se consumerCount >= 1, così consumatori sovrapposti non si disabilitano a vicenda.
  private consumerCount = 0;

  // Sorgente di tempo isolata per testabilità (override nei test unitari).
  private now: () => number = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

  enable(): void {
    // Registra il listener solo sulla prima attivazione (0→1); gli enable successivi solo contano.
    if (++this.consumerCount > 1) return;
    this.listener = (event) => this.handleKey(event);
    document.addEventListener('keydown', this.listener, true);
  }

  disable(): void {
    // Una disable() in eccesso è un no-op sicuro: il contatore non scende sotto 0 (INV-1).
    if (this.consumerCount === 0) return;
    if (--this.consumerCount > 0) return;
    // Ultima disattivazione (1→0): rimuove il listener e resetta il buffer.
    if (this.listener) document.removeEventListener('keydown', this.listener, true);
    this.listener = undefined;
    this.buffer = '';
  }

  // Pubblico per consentire ai test di iniettare KeyboardEvent con timing controllato.
  handleKey(event: KeyboardEvent): void {
    const key = typeof event.key === 'string' ? event.key : '';
    const time = this.now();
    const fast = time - this.lastKeyTime <= this.interKeyThresholdMs;
    this.lastKeyTime = time;

    if (key === 'Enter') {
      this.tryEmit(fast);
      return;
    }
    if (key.length !== 1) return; // ignora tasti non stampabili (Shift, Tab, frecce…) o eventi incompleti
    // Gap troppo lungo → digitazione umana: riparte il burst dal carattere corrente.
    this.buffer = fast ? this.buffer + key : key;
  }

  private tryEmit(fast: boolean): void {
    const code = this.buffer;
    this.buffer = '';
    if (fast && code.length >= this.minCodeLength) this.codeSubject.next(code);
  }
}
